/**
 * @fileoverview Renderiza um preview estilo WhatsApp de um template
 * (header, body, footer, buttons). Usado tanto no builder quanto no edit.
 */

import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { ExternalLink, Phone, CornerDownLeft, FileText, Image as ImageIcon, Video as VideoIcon } from "lucide-react";

const isDark = (theme) => theme.palette.type === "dark";

const useStyles = makeStyles((theme) => ({
  stage: {
    borderRadius: 18,
    padding: theme.spacing(3, 2.5),
    background: isDark(theme)
      ? "radial-gradient(circle at top left, #1f2937 0%, #111827 100%)"
      : "radial-gradient(circle at top left, #d9fdd3 0%, #e4f5db 100%)",
    minHeight: 240,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: "''",
      position: "absolute",
      inset: 0,
      backgroundImage: isDark(theme)
        ? "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)"
        : "radial-gradient(rgba(15,23,42,0.04) 1px, transparent 1px)",
      backgroundSize: "18px 18px",
      pointerEvents: "none",
    },
  },
  bubble: {
    position: "relative",
    maxWidth: "96%",
    background: isDark(theme) ? "#1f2937" : "#FFFFFF",
    borderRadius: "14px 14px 14px 2px",
    padding: theme.spacing(1.25, 1.5, 2, 1.5),
    boxShadow: isDark(theme)
      ? "0 1px 2px rgba(0,0,0,0.35)"
      : "0 1px 1.5px rgba(11,20,26,0.13)",
    color: isDark(theme) ? "#e5e7eb" : "#111b21",
    fontSize: "0.9rem",
    lineHeight: 1.45,
    overflow: "hidden",
    zIndex: 1,
  },
  mediaImage: {
    display: "block",
    width: "100%",
    maxHeight: 180,
    objectFit: "cover",
    borderRadius: 10,
    marginBottom: theme.spacing(1),
  },
  mediaVideo: {
    display: "block",
    width: "100%",
    maxHeight: 180,
    borderRadius: 10,
    marginBottom: theme.spacing(1),
    background: "#000",
  },
  header: {
    fontWeight: 700,
    fontSize: "0.95rem",
    marginBottom: theme.spacing(0.75),
    color: isDark(theme) ? "#f3f4f6" : "#111b21",
  },
  mediaPlaceholder: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    height: 120,
    marginBottom: theme.spacing(1),
    borderRadius: 10,
    background: isDark(theme) ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
    border: isDark(theme)
      ? "1px dashed rgba(255,255,255,0.12)"
      : "1px dashed rgba(15,23,42,0.1)",
    color: isDark(theme) ? "rgba(255,255,255,0.45)" : "#64748b",
    fontSize: "0.78rem",
  },
  body: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  placeholder: {
    background: isDark(theme) ? "rgba(99,102,241,0.22)" : "rgba(99,102,241,0.14)",
    color: isDark(theme) ? "#c7d2fe" : "#4338ca",
    padding: "0 4px",
    borderRadius: 4,
    fontWeight: 600,
    fontSize: "0.85em",
    fontVariantNumeric: "tabular-nums",
  },
  footer: {
    marginTop: theme.spacing(0.75),
    fontSize: "0.78rem",
    color: isDark(theme) ? "rgba(229,231,235,0.55)" : "rgba(17,27,33,0.55)",
  },
  time: {
    position: "absolute",
    bottom: 6,
    right: 10,
    fontSize: "0.65rem",
    color: isDark(theme) ? "rgba(229,231,235,0.5)" : "rgba(17,27,33,0.4)",
    fontVariantNumeric: "tabular-nums",
    pointerEvents: "none",
  },
  // Truque WhatsApp: invisivel, ocupa espaco no fim do texto pra o time
  // absolute nao ficar por cima da ultima palavra/linha do body
  timeGap: {
    display: "inline-block",
    width: 44,
    height: "1em",
  },
  buttons: {
    marginTop: theme.spacing(1),
    width: "96%",
    maxWidth: "96%",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    zIndex: 1,
  },
  btn: {
    background: isDark(theme) ? "#1f2937" : "#FFFFFF",
    color: isDark(theme) ? "#60a5fa" : "#027eb5",
    padding: theme.spacing(1, 1.5),
    textAlign: "center",
    fontWeight: 500,
    fontSize: "0.88rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    borderRadius: 6,
    boxShadow: isDark(theme)
      ? "0 1px 2px rgba(0,0,0,0.35)"
      : "0 1px 1.5px rgba(11,20,26,0.13)",
  },
  emptyStage: {
    textAlign: "center",
    color: isDark(theme) ? "rgba(255,255,255,0.35)" : "#94a3b8",
    fontSize: "0.85rem",
    padding: theme.spacing(4, 2),
    zIndex: 1,
  },
}));

const renderBody = (text, classes, highlightVars = true) => {
  if (!text) return null;
  if (!highlightVars) return text;
  const parts = text.split(/(\{\{\d+\}\})/g);
  return parts.map((part, i) => {
    if (/^\{\{\d+\}\}$/.test(part)) {
      return (
        <span key={i} className={classes.placeholder}>
          {part}
        </span>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

const TemplatePreview = ({ components = [], emptyHint, headerMediaUrl }) => {
  const classes = useStyles();

  const header = components.find((c) => c.type === "HEADER");
  const body = components.find((c) => c.type === "BODY");
  const footer = components.find((c) => c.type === "FOOTER");
  const buttons = components.find((c) => c.type === "BUTTONS");

  const hasContent = header || body || footer || (buttons && buttons.buttons?.length);

  const timeLabel = (() => {
    const d = new Date();
    return `${d.getHours().toString().padStart(2, "0")}:${d
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;
  })();

  if (!hasContent) {
    return (
      <div className={classes.stage}>
        <div className={classes.emptyStage}>
          {emptyHint || "O preview aparece aqui enquanto você edita o template."}
        </div>
      </div>
    );
  }

  return (
    <div className={classes.stage}>
      <div className={classes.bubble}>
        {header && header.format === "TEXT" && header.text && (
          <div className={classes.header}>{renderBody(header.text, classes)}</div>
        )}
        {header && header.format === "IMAGE" &&
          (headerMediaUrl ? (
            <img src={headerMediaUrl} alt="" className={classes.mediaImage} />
          ) : (
            <div className={classes.mediaPlaceholder}>
              <ImageIcon size={16} /> Imagem
            </div>
          ))}
        {header && header.format === "VIDEO" &&
          (headerMediaUrl ? (
            <video
              src={headerMediaUrl}
              className={classes.mediaVideo}
              controls
            />
          ) : (
            <div className={classes.mediaPlaceholder}>
              <VideoIcon size={16} /> Vídeo
            </div>
          ))}
        {header && header.format === "DOCUMENT" && (
          <div className={classes.mediaPlaceholder}>
            <FileText size={16} /> Documento
          </div>
        )}

        {body && body.text && (
          <div className={classes.body}>
            {renderBody(body.text, classes)}
            <span className={classes.timeGap} aria-hidden="true" />
          </div>
        )}

        {footer && footer.text && (
          <div className={classes.footer}>
            {footer.text}
            <span className={classes.timeGap} aria-hidden="true" />
          </div>
        )}

        <div className={classes.time}>{timeLabel}</div>
      </div>

      {buttons && buttons.buttons && buttons.buttons.length > 0 && (
        <div className={classes.buttons}>
          {buttons.buttons.map((btn, i) => (
            <div key={i} className={classes.btn}>
              {btn.type === "URL" && <ExternalLink size={14} />}
              {btn.type === "PHONE_NUMBER" && <Phone size={14} />}
              {btn.type === "QUICK_REPLY" && <CornerDownLeft size={14} />}
              {btn.text || "(sem texto)"}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TemplatePreview;
